import React, { useState } from "react";
import { translate, postKmsData } from "@kaltura/mediaspace-shared-utils";
import { ComposedInput } from "@kaltura/ds-react-components";
import { FormLabel, TextFieldLoading, FormFieldContainer } from "@kaltura/mediaspace-shared-ui/form";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts/ToastsContext";
import { AddFromUrlConfig, AddFromUrlImportModalState, SaveResponse } from "./types";
import { validateUrlStructure } from "./utils";

export interface AddFromUrlImportFormProps {
    allowedExtensions?: string[];
    initialState?: AddFromUrlImportModalState;
    rootClassName?: string;
    onDataChange?: (data: {
        uploadUrl: string | null;
        isLoading: boolean;
        isImporting: boolean;
        error?: string;
    }) => void;
    formId: string;
    onError?: (message: string) => void;
    config: AddFromUrlConfig;
}

/*
 * AddFromUrlImportForm is a reusable component that manages the state and logic for importing media from a URL.
 * It handles URL input, validation, and triggering the server-side creation of a Kaltura entry.
 * The component can be used standalone or within modal wrappers, making it flexible for different contexts.
 */
const AddFromUrlImportForm: React.FC<AddFromUrlImportFormProps> = ({
    allowedExtensions,
    initialState,
    rootClassName = "add-from-url-import-modal",
    onDataChange,
    formId,
    onError,
    config,
}) => {
    // Use initialState if provided, otherwise use default values
    const [url, setUrl] = useState(initialState?.importUrl || "");
    const [isLoading, setIsLoading] = useState(initialState?.isLoading || false);
    const [error, setError] = useState<string | undefined>(initialState?.error);
    const [isImporting, setIsImporting] = useState(false);
    const sendButtonAnalytics = useButtonAnalytics();
    const { showToast } = useToastsContext();

    const handleUrlBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        setIsLoading(true);
        const urlValue = event.currentTarget.value;

        onDataChange?.({
            uploadUrl: urlValue || null,
            isLoading: true,
            isImporting,
            error: undefined,
        });

        setUrl(urlValue);
        const validationResult = validateUrlStructure(urlValue, allowedExtensions);
        setError(validationResult.error);
        setIsLoading(false);

        onDataChange?.({
            uploadUrl: urlValue || null,
            isLoading: false,
            isImporting,
            error: validationResult.error,
        });
    };

    const handleUrlFocus = () => {
        setError(undefined);

        onDataChange?.({
            uploadUrl: url || null,
            isLoading,
            isImporting,
            error: undefined,
        });
    };

    const handleImport = async () => {
        setIsImporting(true);

        onDataChange?.({
            uploadUrl: url || null,
            isLoading,
            isImporting: true,
            error: undefined,
        });

        try {
            const result: SaveResponse = await postKmsData(config.submitUrl, { url });

            if (result.status) {
                showToast({
                    severity: "success",
                    message: config.successMessage || translate("All set! Your import has started."),
                });
                config.onSuccess(result);
            }
            else {
                console.error("Upload failed:", result?.error || "Unknown error");
                onError?.(config.errorMessages?.uploadFailed || translate("Failed to upload entry from URL"));
            }
        }
        catch (error) {
            console.error("Import error:", error);
            onError?.(config.errorMessages?.uploadFailed || translate("Failed to upload entry from URL"));
        }
        finally {
            setIsImporting(false);
            onDataChange?.({
                uploadUrl: url || null,
                isLoading,
                isImporting: false,
                error: undefined,
            });
        }
    };

    const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const validationError = validateUrlStructure(url, allowedExtensions);

        if (validationError.error) {
            setError(validationError.error);
            onError?.(validationError.error);
            return;
        }

        sendButtonAnalytics("Import from URL", ButtonClickAnalyticsType.CREATE);
        handleImport();
    };

    return (
        <form id={formId} onSubmit={handleFormSubmit}>
            <FormFieldContainer sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1 }}>
                <FormLabel
                    fontWeightBold
                    htmlFor={`${rootClassName}-url-input-id`}
                    text={translate("Media URL")}
                    additionalInfo={translate(
                        "The available protocols include: http, https, and ftp; For password-protected URLs, please format it as: protocol://username:password@domain/filepathname."
                    )}
                ></FormLabel>
                <ComposedInput
                    error={!!error}
                    formHelperText={error}
                    defaultValue={url}
                    placeholder={config.urlPlaceholder || translate("e.g. https://media.com/media.mp4")}
                    fullWidth={true}
                    data-testid={rootClassName + "-url-input"}
                    id={`${rootClassName}-url-input-id`}
                    onBlur={handleUrlBlur}
                    onFocus={handleUrlFocus}
                    endAdornment={isLoading ? <TextFieldLoading /> : null}
                />
            </FormFieldContainer>
        </form>
    );
};

export default AddFromUrlImportForm;
